import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useAdmin } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { proxyCoverUrl } from "../utils/coverProxy";
import { 
  BookOpen, LogOut, Plus, Trash2, Edit, Search,
  BarChart3, MessageCircle, BookMarked, Eye, Users, Mail, Star, Bookmark, AtSign
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import AnalyticsTab from "../components/AnalyticsTab";
import ViewersTab from "../components/ViewersTab";

export default function AdminDashboard() {
  const { logout, adminToken } = useAdmin();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [stats, setStats] = useState({ total_books: 0, total_feedback: 0, categories: [], total_views: 0, total_unique_visitors: 0, total_stories: 0, total_contacts: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [feedbacks, setFeedbacks] = useState([]);
  const [stories, setStories] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [nextReads, setNextReads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [adminReply, setAdminReply] = useState({});
  const [activeTab, setActiveTab] = useState("analytics");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    rating: "",
    publisher: "",
    what_to_expect: "",
    key_learnings: "",
    who_should_read: "",
    cover_image: "",
    category: "General",
    homepageTheme: ""
  });

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchBooks = useCallback(async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : "";
      const response = await axios.get(`${API}/books${params}`);
      setBooks(response.data.books);
    } catch (error) {
      console.error("Failed to fetch books:", error);
    }
  }, [searchQuery]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { headers, withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const booksRes = await axios.get(`${API}/books?limit=200`);
      const allFeedbacks = [];
      for (const book of booksRes.data.books) {
        try {
          const fbRes = await axios.get(`${API}/books/${book.book_id}/feedback`);
          if (fbRes.data && fbRes.data.length > 0) {
            fbRes.data.forEach(fb => allFeedbacks.push({ ...fb, book_title: book.title }));
          }
        } catch {}
      }
      setFeedbacks(allFeedbacks.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')));
    } catch {}
  };

  const fetchStories = async () => {
    try {
      const res = await axios.get(`${API}/stories`);
      setStories(res.data);
    } catch {}
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/contacts`);
      setContacts(res.data);
    } catch {}
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/conversations`);
      setConversations(res.data);
    } catch {}
  };

  const fetchNextReads = async () => {
    try {
      const res = await axios.get(`${API}/next-reads`);
      setNextReads(res.data);
    } catch {}
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/subscribers`, { headers, withCredentials: true });
      setSubscribers(res.data);
    } catch {}
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${subscriberId}`, { headers, withCredentials: true });
      toast.success("Subscriber removed");
      fetchSubscribers();
    } catch {
      toast.error("Failed to remove subscriber");
    }
  };

  const handleAdminReply = async (convoId) => {
    const msg = adminReply[convoId];
    if (!msg?.trim()) return;
    try {
      await axios.post(`${API}/conversations/${convoId}/reply`, {
        name: "Lopa",
        message: msg,
        is_admin: true
      });
      toast.success("Reply posted!");
      setAdminReply({ ...adminReply, [convoId]: "" });
      fetchConversations();
    } catch {
      toast.error("Failed to reply.");
    }
  };

  const handleAdminPost = async () => {
    const msg = adminReply["new"];
    if (!msg?.trim()) return;
    try {
      await axios.post(`${API}/conversations`, {
        name: "Lopa",
        message: msg,
        is_admin: true
      });
      toast.success("Posted!");
      setAdminReply({ ...adminReply, new: "" });
      fetchConversations();
    } catch {
      toast.error("Failed to post.");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBooks(), fetchStats(), fetchStories(), fetchContacts(), fetchConversations(), fetchNextReads(), fetchSubscribers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBooks]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const resetForm = () => {
    setFormData({
      title: "",
      author: "",
      rating: "",
      publisher: "",
      what_to_expect: "",
      key_learnings: "",
      who_should_read: "",
      cover_image: "",
      category: "General",
      homepageTheme: ""
    });
  };

  const handleAddBook = async () => {
    if (!formData.title || !formData.author) {
      toast.error("Title and Author are required");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        rating: formData.rating ? parseFloat(formData.rating) : null
      };
      await axios.post(`${API}/admin/books`, data, { headers, withCredentials: true });
      toast.success("Book added successfully!");
      setShowAddDialog(false);
      resetForm();
      fetchBooks();
      fetchStats();
    } catch (error) {
      toast.error("Failed to add book");
    } finally {
      setSaving(false);
    }
  };

  const handleEditBook = async () => {
    if (!formData.title || !formData.author) {
      toast.error("Title and Author are required");
      return;
    }

    setSaving(true);
    try {
      const data = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'rating') {
          if (value !== '' && value !== null) data[key] = parseFloat(value);
        } else if (value !== '' && value !== null) {
          data[key] = value;
        }
      });
      await axios.put(`${API}/admin/books/${selectedBook.book_id}`, data, { headers, withCredentials: true });
      toast.success("Book updated successfully!");
      setShowEditDialog(false);
      setSelectedBook(null);
      resetForm();
      fetchBooks();
    } catch (error) {
      toast.error("Failed to update book");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm("Are you sure you want to delete this book?")) return;

    try {
      await axios.delete(`${API}/admin/books/${bookId}`, { headers, withCredentials: true });
      toast.success("Book deleted");
      fetchBooks();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete book");
    }
  };

  const openEditDialog = (book) => {
    setSelectedBook(book);
    setFormData({
      title: book.title || "",
      author: book.author || "",
      rating: book.rating?.toString() || "",
      publisher: book.publisher || "",
      what_to_expect: book.what_to_expect || "",
      key_learnings: book.key_learnings || "",
      who_should_read: book.who_should_read || "",
      cover_image: book.cover_image || "",
      category: book.category || "General",
      homepageTheme: book.homepageTheme || ""
    });
    setShowEditDialog(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header style={{ background: 'linear-gradient(135deg, #5D4228, #795A3B)', color: '#F5F0E7' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-7 h-7" />
            <span className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Reading Room - Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank">
              <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10">
                View Site
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-alt2)' }}>
                <BookMarked className="w-5 h-5" style={{ color: '#795A3B' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.total_books}</p>
                <p className="text-gray-500 text-xs">Books</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-alt2)' }}>
                <Eye className="w-5 h-5" style={{ color: '#5D4228' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.total_views || 0}</p>
                <p className="text-gray-500 text-xs">Page Views</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--skeleton)' }}>
                <Users className="w-5 h-5" style={{ color: '#5C6B3C' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.total_unique_visitors || 0}</p>
                <p className="text-gray-500 text-xs">Unique Visitors</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--skeleton)' }}>
                <MessageCircle className="w-5 h-5" style={{ color: '#795A3B' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.total_feedback || 0}</p>
                <p className="text-gray-500 text-xs">Feedbacks</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-alt2)' }}>
                <Star className="w-5 h-5" style={{ color: '#C4952B' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.total_stories || 0}</p>
                <p className="text-gray-500 text-xs">Reflect Stories</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-alt2)' }}>
                <Mail className="w-5 h-5" style={{ color: 'var(--muted2)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.total_contacts || 0}</p>
                <p className="text-gray-500 text-xs">Messages</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-alt2)' }}>
                <AtSign className="w-5 h-5" style={{ color: '#5D7A3B' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.total_subscribers || 0}</p>
                <p className="text-gray-500 text-xs">Subscribers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 pb-2">
          {[
            { key: "analytics", label: "Analytics", icon: BarChart3 },
            { key: "viewers", label: "Viewers", icon: Eye },
            { key: "books", label: "Books", icon: BookMarked },
            { key: "subscribers", label: "Subscribers", icon: AtSign },
            { key: "conversations", label: "Conversations", icon: MessageCircle },
            { key: "nextreads", label: "Next Reads", icon: Bookmark },
            { key: "feedback", label: "Reader Comments", icon: MessageCircle },
            { key: "stories", label: "Reflect Stories", icon: Star },
            { key: "contacts", label: "Contact Messages", icon: Mail },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); if (tab.key === 'feedback') fetchFeedbacks(); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-white border border-gray-200 border-b-white -mb-[3px]' : 'text-gray-500 hover:text-gray-700'}`}
              style={activeTab === tab.key ? { color: '#795A3B' } : {}}
              data-testid={`admin-tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Analytics Tab */}
        {activeTab === "analytics" && <AnalyticsTab />}

        {/* Viewers Tab */}
        {activeTab === "viewers" && <ViewersTab />}

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="admin-subscribers-tab">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Email Subscribers</h2>
            <span className="text-sm text-gray-500">{subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''}</span>
          </div>
          {subscribers.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No subscribers yet. Once visitors sign up on the homepage, they'll appear here.</p>
          ) : (
            <div className="space-y-3">
              {subscribers.map((sub) => (
                <div key={sub.subscriber_id} className="flex items-center justify-between border border-gray-100 rounded-lg p-4 hover:bg-gray-50" data-testid={`subscriber-${sub.subscriber_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--bg-alt2)', color: '#5D4228' }}>
                      {sub.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{sub.email}</p>
                      {sub.subscribed_at && <p className="text-xs text-gray-400">{new Date(sub.subscribed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteSubscriber(sub.subscriber_id)} data-testid={`delete-subscriber-${sub.subscriber_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Books Tab */}
        {activeTab === "books" && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-800">Manage Books</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search books..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Button 
                onClick={() => { resetForm(); setShowAddDialog(true); }}
                className="hover:opacity-90 text-white"
                style={{ background: '#5D4228' }}
                data-testid="add-book-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Book
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--highlight)', borderTopColor: 'transparent' }} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Book</th>
                    <th>Author</th>
                    <th>Rating</th>
                    <th>Category</th>
                    <th>Theme</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {books.map((book) => (
                    <tr key={book.book_id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-14 bg-[#EDE5D8] rounded overflow-hidden flex-shrink-0">
                            {book.cover_image && (
                              <img 
                                src={proxyCoverUrl(book.cover_image)} 
                                alt="" 
                                className="w-full h-full object-cover"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                          </div>
                          <span className="font-medium text-gray-800">{book.title}</span>
                        </div>
                      </td>
                      <td className="text-gray-600">{book.author}</td>
                      <td>
                        {book.rating && (
                          <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-sm">
                            {book.rating}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
                          {book.category}
                        </span>
                      </td>
                      <td>
                        {book.homepageTheme && (
                          <span className="px-2 py-1 rounded text-xs font-medium" style={{ background: 'var(--bg-alt2)', color: '#5D4228' }}>
                            {book.homepageTheme}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => openEditDialog(book)}
                            data-testid={`edit-book-${book.book_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteBook(book.book_id)}
                            data-testid={`delete-book-${book.book_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Reader Comments Tab */}
        {activeTab === "feedback" && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Reader Comments & Feedback</h2>
          {feedbacks.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No feedback yet. Loading may take a moment...</p>
          ) : (
            <div className="space-y-4">
              {feedbacks.map((fb, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-semibold text-gray-800">{fb.name || "Anonymous"}</span>
                      <span className="text-gray-400 text-sm ml-3">on <span className="text-[#795A3B] font-medium">{fb.book_title}</span></span>
                    </div>
                    {fb.rating && (
                      <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-sm">{"⭐".repeat(fb.rating)}</span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm">{fb.feedback_text}</p>
                  {fb.created_at && <p className="text-gray-400 text-xs mt-2">{new Date(fb.created_at).toLocaleDateString()}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Reflect Stories Tab */}
        {activeTab === "stories" && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Reflect Stories</h2>
          {stories.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No stories submitted yet.</p>
          ) : (
            <div className="space-y-4">
              {stories.map((s, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-800">{s.name}</span>
                    {s.created_at && <span className="text-gray-400 text-xs">{new Date(s.created_at).toLocaleDateString()}</span>}
                  </div>
                  <p className="text-gray-600 text-sm">{s.story}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Conversations Tab */}
        {activeTab === "conversations" && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Community Conversations</h2>
          
          {/* Admin Post New */}
          <div className="border border-[#E8DCC8] rounded-lg p-4 mb-6 bg-[#F5F0E7]">
            <p className="text-sm font-medium text-gray-600 mb-2">Post as Lopa:</p>
            <div className="flex gap-2">
              <Input
                placeholder="Write a message to the community..."
                value={adminReply["new"] || ""}
                onChange={(e) => setAdminReply({ ...adminReply, new: e.target.value })}
                className="flex-1"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdminPost(); }}
              />
              <Button className="hover:opacity-90 text-white" style={{ background: '#795A3B' }} onClick={handleAdminPost}>Post</Button>
            </div>
          </div>

          {conversations.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No conversations yet.</p>
          ) : (
            <div className="space-y-4">
              {conversations.map((convo) => (
                <div key={convo.convo_id} className="border border-gray-100 rounded-lg overflow-hidden">
                  <div className="p-4 hover:bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-800">{convo.name}</span>
                      {convo.is_admin && <span className="px-2 py-0.5 bg-[#EDE5D8] text-[#795A3B] text-[10px] font-bold rounded-full">LOPA</span>}
                      <span className="text-gray-400 text-xs ml-auto">{convo.created_at ? new Date(convo.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    <p className="text-gray-600 text-sm">{convo.message}</p>
                  </div>
                  
                  {/* Replies */}
                  {convo.replies && convo.replies.length > 0 && (
                    <div className="border-t bg-gray-50 px-4 py-2 space-y-2">
                      {convo.replies.map((r) => (
                        <div key={r.reply_id} className="flex gap-2 ml-4 py-1">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 mt-0.5 ${r.is_admin ? 'bg-[#5D4228]' : 'bg-gray-400'}`}>
                            {r.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700 text-sm">{r.name}</span>
                            {r.is_admin && <span className="ml-1 px-1.5 py-0.5 bg-[#EDE5D8] text-[#795A3B] text-[9px] font-bold rounded-full">LOPA</span>}
                            <p className="text-gray-600 text-sm">{r.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin Reply */}
                  <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
                    <div className="flex gap-2 ml-4">
                      <Input
                        placeholder="Reply as Lopa..."
                        value={adminReply[convo.convo_id] || ""}
                        onChange={(e) => setAdminReply({ ...adminReply, [convo.convo_id]: e.target.value })}
                        className="flex-1 h-8 text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdminReply(convo.convo_id); }}
                      />
                      <Button size="sm" className="hover:opacity-90 text-white h-8" style={{ background: '#795A3B' }} onClick={() => handleAdminReply(convo.convo_id)}>Reply</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Next Reads Tab */}
        {activeTab === "nextreads" && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="admin-next-reads-tab">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Reader Recommendations — What's Your Next Read?</h2>
          {nextReads.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No recommendations submitted yet.</p>
          ) : (
            <div className="space-y-4">
              {nextReads.map((nr) => (
                <div key={nr.read_id} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50" data-testid={`next-read-${nr.read_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: nr.is_admin ? '#5D4228' : '#8A8677' }}>
                        {nr.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-semibold text-gray-800">{nr.name}</span>
                      {nr.is_admin && <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{ background: 'var(--bg-alt2)', color: '#5D4228' }}>LOPA</span>}
                    </div>
                    {nr.created_at && <span className="text-gray-400 text-xs">{new Date(nr.created_at).toLocaleDateString()}</span>}
                  </div>
                  <p className="font-bold text-sm ml-10" style={{ color: '#3A2E1E' }}>{nr.book_title}</p>
                  {nr.note && <p className="text-gray-600 text-sm ml-10 mt-1">{nr.note}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Contact Messages Tab */}
        {activeTab === "contacts" && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Contact Messages</h2>
          {contacts.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No contact messages yet.</p>
          ) : (
            <div className="space-y-4">
              {contacts.map((c, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-semibold text-gray-800">{c.name}</span>
                      <span className="text-gray-400 text-sm ml-2">{c.email}</span>
                      {c.phone && <span className="text-gray-400 text-sm ml-2">• {c.phone}</span>}
                    </div>
                    {c.created_at && <span className="text-gray-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</span>}
                  </div>
                  <p className="text-gray-600 text-sm">{c.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </main>

      {/* Add Book Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Book</DialogTitle>
          </DialogHeader>
          <BookForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddBook} disabled={saving} className="hover:opacity-90 text-white" style={{ background: '#5D4228' }} data-testid="add-book-submit">
              {saving ? "Adding..." : "Add Book"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Book Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Book</DialogTitle>
          </DialogHeader>
          <BookForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditBook} disabled={saving} className="hover:opacity-90 text-white" style={{ background: '#5D4228' }} data-testid="edit-book-submit">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Homepage themes constant
const HOMEPAGE_THEMES = [
  "",
  "Thinking & Mindset",
  "Career & Growth",
  "Leadership & Influence",
  "Life, Reflection & Culture",
  "Stories & Young Minds"
];

// Book Form Component
const BookForm = ({ formData, setFormData }) => (
  <div className="space-y-4 py-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Title *</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Book title"
          data-testid="book-form-title"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Author *</label>
        <Input
          value={formData.author}
          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
          placeholder="Author name"
          data-testid="book-form-author"
        />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Rating</label>
        <Input
          type="number"
          step="0.1"
          min="0"
          max="5"
          value={formData.rating}
          onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
          placeholder="4.5"
          data-testid="book-form-rating"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Publisher</label>
        <Input
          value={formData.publisher}
          onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
          placeholder="Publisher name"
          data-testid="book-form-publisher"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
        <Input
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          placeholder="General"
          data-testid="book-form-category"
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Homepage Theme</label>
        <select
          value={formData.homepageTheme}
          onChange={(e) => setFormData({ ...formData, homepageTheme: e.target.value })}
          className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#795A3B]/30 focus:border-[#795A3B]"
          data-testid="book-form-homepage-theme"
        >
          <option value="">Select a theme...</option>
          {HOMEPAGE_THEMES.filter(Boolean).map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Cover Image URL</label>
        <Input
          value={formData.cover_image}
          onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
          placeholder="https://..."
          data-testid="book-form-cover-image"
        />
      </div>
    </div>
    {formData.cover_image && (
      <div className="flex items-center gap-3">
        <div className="w-16 h-24 bg-[#EDE5D8] rounded overflow-hidden border border-gray-200">
          <img src={formData.cover_image} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <span className="text-xs text-gray-400">Cover preview</span>
      </div>
    )}
    <div>
      <label className="text-sm font-medium text-gray-700 mb-1 block">What to Expect</label>
      <Textarea
        value={formData.what_to_expect}
        onChange={(e) => setFormData({ ...formData, what_to_expect: e.target.value })}
        placeholder="Brief description of what readers can expect..."
        rows={3}
        data-testid="book-form-what-to-expect"
      />
    </div>
    <div>
      <label className="text-sm font-medium text-gray-700 mb-1 block">Key Learnings</label>
      <Textarea
        value={formData.key_learnings}
        onChange={(e) => setFormData({ ...formData, key_learnings: e.target.value })}
        placeholder="Key takeaways from the book..."
        rows={3}
        data-testid="book-form-key-learnings"
      />
    </div>
    <div>
      <label className="text-sm font-medium text-gray-700 mb-1 block">Who Should Read This</label>
      <Textarea
        value={formData.who_should_read}
        onChange={(e) => setFormData({ ...formData, who_should_read: e.target.value })}
        placeholder="Target audience for this book..."
        rows={2}
        data-testid="book-form-who-should-read"
      />
    </div>
  </div>
);
